import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { FileText, Download } from "lucide-react";
import { confidenceWord, humanise } from "@/components/research-hub/labels";

// One readable line that leads with the conviction/horizon/portfolio reasoning,
// instead of leaving it crammed into the unformatted badge row below.
function houseViewLine(item: {
  conviction?: number | null;
  time_horizon?: string | null;
  portfolio_level?: number | null;
  primary_axis?: string | null;
}): string {
  const parts: string[] = [];
  if (item.conviction != null) {
    parts.push(`${confidenceWord(item.conviction)} view`);
  }
  if (item.primary_axis) parts.push(humanise(item.primary_axis).toLowerCase());
  if (item.time_horizon) parts.push(`${humanise(item.time_horizon).toLowerCase()} horizon`);
  if (item.portfolio_level != null) parts.push(`portfolio ${item.portfolio_level}`);
  if (parts.length === 0) return "";
  const [head, ...rest] = parts;
  const sentence = rest.length ? `${head} · ${rest.join(" · ")}` : head;
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

const AXES = ["all", "asset_class", "geography", "sector", "theme", "instrument_type", "custom"] as const;

export default function HouseViewFeed() {
  const [q, setQ] = useState("");
  const [axis, setAxis] = useState<string>("all");
  const [portfolio, setPortfolio] = useState<string>("all");

  const { data: items, isLoading } = useQuery({
    queryKey: ["house_view_feed", { q, axis, portfolio }],
    queryFn: async () => {
      let query = supabase
        .from("research_items")
        .select("*")
        .eq("status", "published")
        .order("published_at", { ascending: false })
        .limit(100);
      if (axis !== "all") query = query.eq("primary_axis", axis as any);
      if (portfolio !== "all") query = query.eq("portfolio_level", Number(portfolio));
      if (q) query = query.ilike("title", `%${q}%`);
      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
  });

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-primary">House View</h1>
        <p className="text-muted-foreground text-sm">
          Published investment research and house views from the Investment Team.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search title..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-xs"
        />
        <Select value={axis} onValueChange={setAxis}>
          <SelectTrigger className="w-44"><SelectValue placeholder="Axis" /></SelectTrigger>
          <SelectContent>
            {AXES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={portfolio} onValueChange={setPortfolio}>
          <SelectTrigger className="w-44"><SelectValue placeholder="Portfolio" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All portfolios</SelectItem>
            {[1, 2, 3, 4, 5].map((p) => (
              <SelectItem key={p} value={String(p)}>P{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading...</p>}
      {items && items.length === 0 && <p className="text-sm text-muted-foreground">No entries</p>}

      <div className="grid gap-3">
        {items?.map((item) => (
          <Card key={item.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/partner/house-view/${item.id}`}
                    className="font-semibold text-primary hover:underline flex items-center gap-2"
                  >
                    <FileText className="h-4 w-4" />
                    {item.title}
                  </Link>
                  {houseViewLine(item) && (
                    <p className="text-sm font-medium text-foreground/90 mt-1">
                      {houseViewLine(item)}
                    </p>
                  )}
                  {(item.ai_summary || item.summary) && (
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                      {item.ai_summary || item.summary}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-muted-foreground">
                    <Badge variant="secondary">Published</Badge>
                    {item.published_at && (
                      <span>{format(new Date(item.published_at), "dd/MM/yyyy")}</span>
                    )}
                  </div>
                </div>
                <Button asChild variant="outline" size="sm">
                  <Link to={`/partner/house-view/${item.id}`}>
                    <Download className="h-4 w-4 mr-1" /> View
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
